import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'

/**
 * A small accessible single-input dialog (create/rename/insert). For a one-line field Enter
 * submits; for `multiline` (a textarea) Enter inserts a newline and Ctrl/⌘+Enter submits.
 * Escape cancels.
 *
 * When `onSubmit` returns a Promise the dialog is BUSY until it settles (AB#229): the first
 * activation (click or Enter) disables every control, shows a spinner in the confirm button and
 * announces `busyLabel`; further activations are ignored (a ref guards it synchronously, so a
 * double click inside one frame still submits once) and Escape / the backdrop do not close it.
 * On rejection the form comes back with its values and the error is shown inline for a retry.
 * On success the caller is expected to unmount the dialog, as before.
 */
export function PromptModal({
  title,
  label,
  initialValue = '',
  confirmLabel,
  placeholder,
  multiline = false,
  choiceLabel,
  choices,
  extraLabel,
  onExtra,
  onSubmit,
  onClose,
  busyLabel,
}: {
  title: string
  label: string
  initialValue?: string
  confirmLabel?: string
  placeholder?: string
  multiline?: boolean
  /** Group label for `choices` — required whenever they are given (the radios need a name). */
  choiceLabel?: string
  /**
   * An optional second axis alongside the text field, as a radio group. Added for "add an item",
   * where the name is not the whole decision any more: a part's KIND (prose or grid) is fixed at
   * creation and cannot be changed afterwards, so it has to be chosen here. Kept inside this
   * dialog rather than spawned as a second one — two modals to create one item is worse.
   *
   * `hint` is an optional one-line explanation under a choice (the artefact type's "exports to…"),
   * tied to its radio with aria-describedby. The FIRST choice is preselected.
   */
  choices?: Array<{ value: string; label: string; hint?: string }>
  /**
   * An optional secondary action rendered next to Cancel, for the one thing an empty value used to
   * mean and no longer can: confirm is disabled on an empty field, so "clear this" needs a button
   * of its own (the Link dialog's "Remove link"). Omit both and the button is not rendered — which
   * is every other call site.
   */
  extraLabel?: string
  onExtra?: () => void
  onSubmit: (value: string, choice?: string) => void | Promise<unknown>
  onClose: () => void
  /** Announced while a Promise-returning `onSubmit` is pending ("Creating…"); defaults to a generic wording. */
  busyLabel?: string
}) {
  const { t } = useLang()
  const [value, setValue] = useState(initialValue)
  const [choice, setChoice] = useState(choices?.[0]?.value ?? '')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const hintId = useId()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The synchronous guard: React state alone lets a second click land before the re-render.
  const busyRef = useRef(false)
  // The caller usually unmounts the dialog on success before its promise settles — no state then.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    inputRef.current?.focus()
    inputRef.current?.select()
    return () => prev?.focus()
  }, [])

  const submit = (): void => {
    if (busyRef.current) return
    const v = value.trim()
    if (!v) return
    const result = onSubmit(v, choices ? choice : undefined)
    if (!(result instanceof Promise)) return
    busyRef.current = true
    setBusy(true)
    setError(null)
    result.then(
      () => {
        busyRef.current = false
        if (mountedRef.current) setBusy(false)
      },
      (e: unknown) => {
        busyRef.current = false
        if (!mountedRef.current) return
        setBusy(false)
        const server = (e as { serverMessage?: unknown } | null)?.serverMessage
        setError(typeof server === 'string' && server ? server : t('prompt_failed'))
      },
    )
  }
  const close = (): void => {
    if (!busyRef.current) onClose()
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close}>
      <div
        className="modal prompt-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-busy={busy || undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close()
        }}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={close} disabled={busy}>✕</button>
        </div>
        <div className="modal-body prompt-body">
          <label className="prompt-label">
            {label}
            {multiline ? (
              <textarea
                ref={(el) => {
                  inputRef.current = el
                }}
                className="prompt-input prompt-textarea"
                rows={4}
                value={value}
                disabled={busy}
                placeholder={placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault()
                    submit()
                  }
                }}
              />
            ) : (
              <input
                ref={(el) => {
                  inputRef.current = el
                }}
                className="prompt-input"
                value={value}
                disabled={busy}
                placeholder={placeholder}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submit()
                  }
                }}
              />
            )}
          </label>
          {choices && choices.length > 0 && (
            <fieldset className="prompt-choice" disabled={busy}>
              <legend>{choiceLabel}</legend>
              {choices.map((c) =>
                c.hint ? (
                  // The hint sits OUTSIDE the label so the radio's name stays the short label;
                  // it is announced as the description instead.
                  <div key={c.value} className="prompt-choice-opt">
                    <label>
                      <input
                        type="radio"
                        name="prompt-choice"
                        value={c.value}
                        checked={choice === c.value}
                        aria-describedby={`${hintId}-${c.value}`}
                        onChange={() => setChoice(c.value)}
                      />
                      {c.label}
                    </label>
                    <span id={`${hintId}-${c.value}`} className="prompt-choice-hint">{c.hint}</span>
                  </div>
                ) : (
                  <label key={c.value}>
                    <input
                      type="radio"
                      name="prompt-choice"
                      value={c.value}
                      checked={choice === c.value}
                      onChange={() => setChoice(c.value)}
                    />
                    {c.label}
                  </label>
                ),
              )}
            </fieldset>
          )}
          {error && (
            <p className="prompt-error" role="alert">{error}</p>
          )}
          <p className="visually-hidden" role="status">{busy ? (busyLabel ?? t('prompt_busy')) : ''}</p>
          <div className="prompt-acts">
            {extraLabel && onExtra && (
              <button className="mini no" onClick={onExtra} disabled={busy}>{extraLabel}</button>
            )}
            <button className="mini ghost" onClick={close} disabled={busy}>{t('cancel')}</button>
            <button className="mini ok prompt-confirm" onClick={submit} disabled={busy || !value.trim()}>
              {busy && <span className="brand-spinner" aria-hidden="true" />}
              {confirmLabel ?? t('save')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
